import React, { useEffect, useState } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { useAppServices } from "../../../hook/services";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";

const BulkMessages = () => {
  const { locationId } = useParams();
  const AppService = useAppServices();

  const [data, setData] = useState({
    fromNumber: null,
    toNumberType: "contacts", // NEW field
    toNumbers: [],
    message: "",
    tags: [],
    dobFilter: "none",
    dobDate: "",
    dobMonth: "",
    dobRange: { from: "", to: "" },
    assignedUsers: [],
    excludeUsers: [],
    age: { condition: ">", value: "" },
  });

  const [loading, setLoading] = useState(true);
  const [numbers, setNumbers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [customValues, setCustomValues] = useState([]);
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);

  // Field menu state
  const [activeTextarea, setActiveTextarea] = useState("main");
  const [showFieldMenu, setShowFieldMenu] = useState(false);
  const [fieldMenuView, setFieldMenuView] = useState("main");
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch SIM Numbers
  const fetchSimNumbers = async () => {
    try {
      const { response } = await AppService.sim.get({
        query: `locationId=${locationId}`,
      });
      if (response) {
        setNumbers(
          response.data.map((num) => ({
            ...num,
            label: num.sim_number,
            value: num._id,
          }))
        );
      }
    } catch {
      console.log("Failed to fetch SIM numbers.");
    }
  };

  // Fetch Contacts & Filters Data
  const fetchGhlData = async () => {
    try {
      const { response } = await AppService.ghlapis.get_bulk_sms_data({
        query: `account_id=${locationId}`,
      });
      if (response) {
        setContacts(
          response.contacts.map((c) => ({
            ...c,
            label: c.phone,
            value: c.phone,
          }))
        );
        setCustomFields(
          response.customFields?.map((item) => ({
            label: item.name,
            value: `{{${item.fieldKey}}}`,
            id: item.id,
          }))
        );
        setCustomValues(
          response.customValues?.map((item) => ({
            label: item.name,
            value: `${item.fieldKey}`,
            fieldValue: item.value,
          }))
        );
        setUsers(
          response.users?.map((item) => ({
            label: item.name,
            value: item.id,
          }))
        );
        setTags(
          response.tags?.map((item) => ({
            label: item.name,
            value: item.id,
          }))
        );
      }
    } catch {
      console.log("Failed to fetch contacts.");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchSimNumbers(), fetchGhlData()]);
      setLoading(false);
    };
    fetchData();
  }, []);

  // State Updates
  const handleChange = (field, value) =>
    setData((prev) => ({ ...prev, [field]: value }));

  const handleNestedChange = (parent, field, value) =>
    setData((prev) => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value },
    }));

  // Insert Custom Field
  const insertField = (fieldValue) => {
    handleChange("message", data.message + " " + fieldValue);
    setShowFieldMenu(false);
    setFieldMenuView("main");
    setSearchTerm("");
  };

  // Field Menu
  const openFieldMenu = (textareaId = "main") => {
    setActiveTextarea(textareaId);
    setShowFieldMenu(!showFieldMenu);
    setFieldMenuView("main");
    setSearchTerm("");
  };

  const FieldInsertionMenu = () => {
    const filteredFields = customFields.filter((i) =>
      i.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const filteredValues = customValues.filter((i) =>
      i.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <div
        className="dropdown-menu show shadow border-0 p-2"
        style={{ width: "280px", maxHeight: "300px", overflowY: "auto" }}
      >
        {fieldMenuView === "main" ? (
          <div className="list-group list-group-flush">
            <button
              type="button"
              className="list-group-item list-group-item-action py-2"
              onClick={() => setFieldMenuView("fields")}
            >
              <i className="bi bi-person-badge me-2"></i> Custom Fields →
            </button>
            <button
              type="button"
              className="list-group-item list-group-item-action py-2"
              onClick={() => setFieldMenuView("values")}
            >
              <i className="bi bi-tags me-2"></i> Custom Values →
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary mb-2"
              onClick={() => {
                setFieldMenuView("main");
                setSearchTerm("");
              }}
            >
              ← Back
            </button>
            <input
              type="text"
              className="form-control form-control-sm mb-2"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <h6 className="dropdown-header">
              {fieldMenuView === "fields" ? "Custom Fields" : "Custom Values"}
            </h6>
            {(fieldMenuView === "fields" ? filteredFields : filteredValues).map(
              (item) => (
                <button
                  key={item.value}
                  className="dropdown-item"
                  onClick={() => insertField(item.value)}
                >
                  {item.label}
                </button>
              )
            )}
          </>
        )}
      </div>
    );
  };
  // contacts = your contacts array

  function filterContacts(contacts, filters) {
    const today = new Date();

    return contacts.filter((contact) => {
      // 1. Tags filter
      if (filters.tags?.length > 0) {
        const hasTag = filters.tags.some((tag) =>
          contact.tags?.includes(tag.label)
        );
        if (!hasTag) return false;
      }

      // 2. Assigned users filter
      if (filters.assignedUsers?.length > 0) {
        if (!filters.assignedUsers.includes(contact.assignedTo)) {
          return false;
        }
      }

      // 3. Exclude users filter
      if (filters.excludeUsers?.length > 0) {
        if (filters.excludeUsers.includes(contact.assignedTo)) {
          return false;
        }
      }

      // 4. Date of Birth filter
      if (
        contact.dateOfBirth &&
        filters.dobFilter &&
        filters.dobFilter !== "none"
      ) {
        const dob = new Date(contact.dateOfBirth);

        if (filters.dobFilter === "equals" && filters.dobDate) {
          if (dob.toISOString().slice(0, 10) !== filters.dobDate) return false;
        }

        if (filters.dobFilter === "month" && filters.dobMonth) {
          const month = dob.getMonth() + 1; // 1–12
          if (month.toString() !== filters.dobMonth) return false;
        }

        if (
          filters.dobFilter === "range" &&
          filters.dobRange?.from &&
          filters.dobRange?.to
        ) {
          const from = new Date(filters.dobRange.from);
          const to = new Date(filters.dobRange.to);
          if (dob < from || dob > to) return false;
        }
      }

      // 5. Age filter
      if (
        contact.dateOfBirth &&
        filters.age?.value &&
        filters.age?.value != ""
      ) {
        const dob = new Date(contact.dateOfBirth);
        let age = today.getFullYear() - dob.getFullYear();
        const m = today.getMonth() - dob.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
          age--;
        }

        const cond = filters.age.condition;
        const val = parseInt(filters.age.value, 10);

        if (cond === ">" && !(age > val)) return false;
        if (cond === "<" && !(age < val)) return false;
        if (cond === "=" && !(age === val)) return false;
        if (cond === ">=" && !(age >= val)) return false;
        if (cond === "<=" && !(age <= val)) return false;
      }

      return true;
    });
  }
  function personalizeMessage(contact, template) {
    let message = template;

    if (Array.isArray(contact.customFields)) {
      contact.customFields.forEach((field) => {
        const filteredField = customFields.find((item) => item.id == field.id);
        const placeholder = filteredField.value;
        message = message.replaceAll(placeholder, field.value || "");
      });
    }

    return message;
  }
  function replaceCustomValues(messagetext) {
    let message = messagetext;
    customValues.forEach((field) => {
      const placeholder = field.value;
      message = message.replaceAll(placeholder, field.fieldValue || "");
    });
    return message;
  }
  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    let message = data.message.trim();
    if (!data.fromNumber && !message) {
      Swal.fire("Validation Error", "Please fill required fields.", "warning");
      return;
    }
    message = replaceCustomValues(message);
    let filteredContacts = data.toNumbers;
    if (data.toNumberType == "filters") {
      filteredContacts = filterContacts(contacts, data);
      if (!filteredContacts.length) {
        Swal.fire("No contact found against these filters.", "warning");
        return;
      }
    }
    if (!filteredContacts.length) {
      Swal.fire("Please select atleast one number from to contact.", "warning");
      return;
    }
    const messages = filteredContacts.map((contact) => ({
      phone: contact.phone,
      message: personalizeMessage(contact, message),
    }));

    const payload = {
      fromNumber: data.fromNumber,
      messages: messages,
    };
    try {
      const { response } = await AppService.webhook.bulkmessage({ payload });
      if (response?.success) {
        Swal.fire("Sent", "Messages sent successfully.", "success");
        setData((prev) => ({ ...prev, toNumbers: [], message: "" }));
      } else throw new Error("Failed to send");
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to send", "error");
    }
  };

  return (
    <div className="container py-4">
      <div className="mx-auto" style={{ maxWidth: "70vw" }}>
        <div className="card shadow-sm border-0">
          <div className="card-body p-4">
            <h4 className="mb-4 fw-bold text-primary">📤 Send Bulk SMS</h4>

            {loading ? (
              <div className="text-center py-5">
                <div
                  className="spinner-border text-primary"
                  role="status"
                ></div>
                <p className="text-muted mt-3">Loading...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {/* FROM Number */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">From</label>
                  <Select
                    options={numbers}
                    value={data.fromNumber}
                    onChange={(val) => handleChange("fromNumber", val)}
                    placeholder="Select sender number"
                  />
                </div>

                {/* NEW: From Number Type */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    From Number Type
                  </label>
                  <select
                    className="form-select"
                    value={data.toNumberType}
                    onChange={(e) =>
                      handleChange("toNumberType", e.target.value)
                    }
                  >
                    <option value="contacts">Contacts</option>
                    <option value="filters">Filters</option>
                  </select>
                </div>

                {/* TO Numbers - Only if Contacts */}
                {data.toNumberType === "contacts" && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">To</label>
                    <Select
                      isMulti
                      options={contacts}
                      value={data.toNumbers}
                      onChange={(val) => handleChange("toNumbers", val)}
                      placeholder="Select recipients"
                    />
                  </div>
                )}

                {/* Filters - Only if Filters selected */}
                {data.toNumberType === "filters" && (
                  <>
                    {/* Tags */}
                    <div className="mb-3">
                      <label className="form-label">Tags</label>
                      <CreatableSelect
                        isMulti
                        options={tags}
                        value={data.tags}
                        onChange={(val) => handleChange("tags", val)}
                        placeholder="Type to add tags..."
                      />
                    </div>

                    {/* DOB */}
                    <div className="mb-3">
                      <label className="form-label">DOB Filter</label>
                      <select
                        className="form-select"
                        value={data.dobFilter}
                        onChange={(e) =>
                          handleChange("dobFilter", e.target.value)
                        }
                      >
                        <option value="none">None</option>
                        <option value="thisWeek">This Week</option>
                        <option value="thisMonth">This Month</option>
                        <option value="inMonth">In Month</option>
                        <option value="onDate">On Date</option>
                        <option value="range">Date Range</option>
                      </select>
                    </div>

                    {data.dobFilter === "onDate" && (
                      <input
                        type="date"
                        className="form-control mb-3"
                        onChange={(e) =>
                          handleChange("dobDate", e.target.value)
                        }
                      />
                    )}
                    {data.dobFilter === "inMonth" && (
                      <input
                        type="month"
                        className="form-control mb-3"
                        onChange={(e) =>
                          handleChange("dobMonth", e.target.value)
                        }
                      />
                    )}
                    {data.dobFilter === "range" && (
                      <div className="d-flex gap-2 mb-3">
                        <input
                          type="date"
                          className="form-control"
                          onChange={(e) =>
                            handleNestedChange(
                              "dobRange",
                              "from",
                              e.target.value
                            )
                          }
                        />
                        <input
                          type="date"
                          className="form-control"
                          onChange={(e) =>
                            handleNestedChange("dobRange", "to", e.target.value)
                          }
                        />
                      </div>
                    )}

                    {/* Users */}
                    <div className="mb-3">
                      <label className="form-label">Assigned Users</label>
                      <Select
                        isMulti
                        options={users}
                        value={data.assignedUsers}
                        onChange={(val) => handleChange("assignedUsers", val)}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Exclude Users</label>
                      <Select
                        isMulti
                        options={users}
                        value={data.excludeUsers}
                        onChange={(val) => handleChange("excludeUsers", val)}
                      />
                    </div>

                    {/* Age */}
                    <div className="mb-3 d-flex gap-2">
                      <select
                        className="form-select w-auto"
                        value={data.age.condition}
                        onChange={(e) =>
                          handleNestedChange("age", "condition", e.target.value)
                        }
                      >
                        <option value=">">Greater Than</option>
                        <option value="<">Less Than</option>
                      </select>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Age"
                        value={data.age.value}
                        onChange={(e) =>
                          handleNestedChange("age", "value", e.target.value)
                        }
                      />
                    </div>
                  </>
                )}

                {/* Message */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">Message</label>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">
                      Personalize with custom fields
                    </small>
                    <div className="dropdown">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        type="button"
                        onClick={() => openFieldMenu("main")}
                      >
                        <i className="bi bi-braces"></i> Insert Field
                      </button>
                      {showFieldMenu && activeTextarea === "main" && (
                        <FieldInsertionMenu />
                      )}
                    </div>
                  </div>
                  <textarea
                    className="form-control"
                    rows="5"
                    placeholder="Write campaign message..."
                    value={data.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    required
                  />
                </div>

                {/* Send */}
                <button
                  type="submit"
                  className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
                  disabled={!data.fromNumber || !data.message.trim()}
                >
                  <i className="bi bi-send"></i> Send SMS
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkMessages;
